import { router } from 'expo-router';
import { useCallback, useMemo } from 'react';

import { useOffersLoadingWithTrailingSettle } from '@fhs/cart/src/hooks/use-offers-loading-with-trailing-settle';
import { useCartValues } from '@fhs/cart/src/state/cart-store';
import { Schema, client, queryClient, useQuery } from '@fhs/client';
import { usePathnameRef } from '@fhs/utils';
import {
  ILoyaltyEstimatePointsInput,
  ILoyaltyQuest,
  ILoyaltyQuestUi,
  ILoyaltyQuestsInput,
  ILoyaltyQuestsQuery,
  ILoyaltyQuestsQueryVariables,
  ILoyaltyQuestsUiQuery,
  ILoyaltyQuestsUiQueryVariables,
  ILoyaltyRewardTier,
  ILoyaltyRewardTiersQuery,
  ILoyaltyRewardTiersQueryVariables,
  ILoyaltyUserEstimatedPointsEarnedQuery,
  ILoyaltyUserEstimatedPointsEarnedQueryVariables,
  ILoyaltyUserTransactionsQuery,
  ILoyaltyUserTransactionsQueryVariables,
  ITransactionFragment,
  LoyaltyQuestsDocument,
  LoyaltyQuestsUiDocument,
  LoyaltyRewardTiersDocument,
  LoyaltyUserEstimatedPointsEarnedDocument,
  LoyaltyUserTransactionsDocument,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import useDebouncedEffect from '@fhs-legacy/frontend/src/hooks/use-debounce-effect';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';
import { frontendToBackendCartEntryMap } from '@fhs-legacy/frontend/src/utils/cart';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { useApplyOffer } from '../hooks/use-apply-offer';
import { useLoyaltyCartInput } from '../hooks/use-loyalty-cart-input';
import { useLoyaltyLegacyStates } from '../hooks/use-loyalty-legacy-states';
import { useRemoveOffer } from '../hooks/use-remove-offer';

export const loyaltyIncentivesQueryKeys = {
  all: ['loyaltyIncentives'] as const,
  params: ({
    storeId,
    serviceMode,
    loyaltyId,
  }: {
    storeId: string;
    serviceMode: string;
    loyaltyId?: string;
  }) => [...loyaltyIncentivesQueryKeys.all, storeId, serviceMode, loyaltyId] as const,
};

export type LoyaltyIncentiveOffer = Schema['OfferListItem']['type'] & {
  applyOffer: () => Promise<void>;
  removeOffer: () => void;
  seeDetails: () => void;
  isApplied: boolean;
};

export const useLoyaltyIncentives = () => {
  const { store, serviceMode, loyaltyUserReady, loyaltyId, selectedOffer } =
    useLoyaltyLegacyStates();

  const { applyOffer } = useApplyOffer();
  const { removeOffer } = useRemoveOffer();

  const { data, ...props } = useQuery({
    enabled: loyaltyUserReady && !!store.number && !!serviceMode,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryKey: loyaltyIncentivesQueryKeys.params({
      serviceMode: serviceMode ?? '',
      storeId: store.number ?? '',
      loyaltyId,
    }),
    meta: { persist: false },
    queryFn: async () => {
      const response = await client.queries.getLoyaltyIncentives({
        storeNumber: store.number ?? '',
        serviceMode: serviceMode ?? '',
        loyaltyId,
      });
      if (response.errors) {
        throw response.errors;
      }

      return response.data;
    },
  });

  const prepareOfferData = useCallback(
    (offers: Schema['OfferListItem']['type'][]) => {
      return offers.map<LoyaltyIncentiveOffer>(offer => ({
        ...offer,
        isApplied: selectedOffer?._id === offer.id,
        applyOffer: async () => {
          await applyOffer(offer);
        },
        removeOffer: () => {
          removeOffer();
        },
        seeDetails: () => {
          router.navigate(routes.offersV2Details + `/${offer.id}`);
        },
      }));
    },
    [selectedOffer, applyOffer]
  );

  const loyaltyIncentivesData = useMemo(
    () => ({
      ...data,
      offers: prepareOfferData(data?.offers ?? []),
    }),
    [data, prepareOfferData]
  );

  return {
    ...props,
    data: loyaltyIncentivesData,
  };
};

export const useOfferIncentive = (offerId: string) => {
  const { data, ...props } = useLoyaltyIncentives();

  const currentOffer = useMemo(
    () => data?.offers?.find(offer => offer && offer.id === offerId),
    [data?.offers, offerId]
  );
  return {
    ...props,
    data: currentOffer,
  };
};

export const offerDetailUiQueryKeys = {
  all: ['offerDetailUi'] as const,
  params: ({
    storeId,
    serviceMode,
    loyaltyId,
    offerId,
    cartEntriesV1,
  }: {
    storeId: string;
    serviceMode: string;
    loyaltyId?: string;
    offerId: string;
    cartEntriesV1: any;
  }) =>
    [
      ...offerDetailUiQueryKeys.all,
      offerId,
      storeId,
      serviceMode,
      loyaltyId,
      cartEntriesV1,
    ] as const,
};

export const useOfferDetailUi = (offerId: string) => {
  const { store, serviceMode, loyaltyUserReady, loyaltyId } = useLoyaltyLegacyStates();
  const { cartEntries } = useCartValues();
  const backendCartEntries = useMemo(
    () => (cartEntries ? cartEntries.map(frontendToBackendCartEntryMap) : []),
    [cartEntries]
  );
  // this a temp cartEntriesV1, we need to include from state
  const cartEntriesV1 = useMemo(() => JSON.stringify(backendCartEntries), [backendCartEntries]);

  return useQuery({
    enabled: loyaltyUserReady && !!store.number && !!serviceMode,
    refetchOnWindowFocus: false,
    queryKey: offerDetailUiQueryKeys.params({
      cartEntriesV1,
      serviceMode: serviceMode ?? '',
      storeId: store.number ?? '',
      loyaltyId,
      offerId,
    }),
    queryFn: async () => {
      const response = await client.queries.getOfferDetailUi({
        storeNumber: store.number ?? '',
        serviceMode: serviceMode ?? '',
        loyaltyId,
        offerId,
        cartEntriesV1,
      });
      if (response.errors) {
        throw response.errors;
      }

      return response.data;
    },
  });
};

export const challengesQueryKey = ['challenges'];

export const challengesUiQueryKeys = {
  all: [...challengesQueryKey, 'challengesUi'] as const,
  loyaltyUser: ({
    loyaltyId,
    isChallengeUiEnabled = true,
  }: {
    loyaltyId?: string;
    isChallengeUiEnabled?: boolean;
  }) => [...challengesUiQueryKeys.all, loyaltyId, isChallengeUiEnabled] as const,
};

export const useChallengesUi = () => {
  const { loyaltyId } = useLoyaltyLegacyStates();
  const isChallengeUiEnabled = useFlag('enable-challenge-ui');

  return useQuery({
    enabled: Boolean(isChallengeUiEnabled && !!loyaltyId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryKey: challengesUiQueryKeys.loyaltyUser({
      loyaltyId,
      isChallengeUiEnabled,
    }),
    queryFn: async () => {
      const response = await legacyClient.query<
        ILoyaltyQuestsUiQuery,
        ILoyaltyQuestsUiQueryVariables
      >({
        query: LoyaltyQuestsUiDocument,
        variables: { loyaltyId },
        fetchPolicy: 'no-cache',
      });
      if (response.errors) {
        throw response.errors;
      }
      return response.data.loyaltyQuestsUIV2 as ILoyaltyQuestUi[];
    },
  });
};

export const useChallenge = (challengeId: string) => {
  const { data, ...props } = useChallengesUi();

  const currentChallenge = useMemo(
    () => data?.find(challenge => challenge && challenge.id === challengeId),
    [data, challengeId]
  );
  return {
    ...props,
    data: currentChallenge,
  };
};

const evaluateChallenges = async (loyaltyId: string, where: ILoyaltyQuestsInput) => {
  const response = await legacyClient.query<ILoyaltyQuestsQuery, ILoyaltyQuestsQueryVariables>({
    query: LoyaltyQuestsDocument,
    variables: { loyaltyId, where },
    fetchPolicy: 'no-cache',
  });
  if (response.errors) {
    throw response.errors;
  }
  return response.data.loyaltyQuestsV2 as ILoyaltyQuest[];
};

export const evaluateChallengesQueryKeys = {
  all: [...challengesQueryKey, 'evaluateChallenges'] as const,
  loyaltyUser: ({
    loyaltyId,
    isChallengeUiEnabled = true,
  }: {
    loyaltyId?: string;
    isChallengeUiEnabled?: boolean;
  }) => [...evaluateChallengesQueryKeys.all, loyaltyId, isChallengeUiEnabled] as const,
  cart: ({
    loyaltyId,
    isChallengeUiEnabled = true,
    where,
  }: {
    loyaltyId?: string;
    isChallengeUiEnabled?: boolean;
    where?: ILoyaltyQuestsInput;
  }) =>
    [
      ...evaluateChallengesQueryKeys.loyaltyUser({ loyaltyId, isChallengeUiEnabled }),
      where,
    ] as const,
};

const evaluateChallengesQueryOptions = ({
  loyaltyId,
  isChallengeUiEnabled,
  where,
}: {
  loyaltyId: string;
  isChallengeUiEnabled: boolean;
  where: ILoyaltyQuestsInput;
}) => ({
  queryKey: evaluateChallengesQueryKeys.cart({ loyaltyId, isChallengeUiEnabled, where }),
  queryFn: async () => await evaluateChallenges(loyaltyId, where),
});

export const useEvaluateChallenges = () => {
  const { loyaltyId } = useLoyaltyLegacyStates();
  const where = useLoyaltyCartInput();
  const isOffersEvaluating = useOffersLoadingWithTrailingSettle();

  const isChallengeUiEnabled = useFlag('enable-challenge-ui');

  return useQuery({
    // Wait for offers to settle so the minimum spend is checked against the discounted subtotal
    enabled: isChallengeUiEnabled && !!loyaltyId && !isOffersEvaluating,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: previousData => previousData ?? [],
    ...evaluateChallengesQueryOptions({ loyaltyId, isChallengeUiEnabled, where }),
  });
};

const PREFETCH_EVALUATE_CHALLENGES_DEBOUNCE_MS = 250;

/**
 * Evaluates the challenges in the background while the cart changes, so the cart challenge message is ready when the
 * cart opens. Uses the same input and query key as useEvaluateChallenges. Skipped in the cart, where
 * useEvaluateChallenges already evaluates them.
 */
export const usePrefetchEvaluateChallenges = () => {
  const { getCurrentPathname } = usePathnameRef();
  const { loyaltyId } = useLoyaltyLegacyStates();
  const where = useLoyaltyCartInput();
  const isOffersEvaluating = useOffersLoadingWithTrailingSettle();

  const isChallengeUiEnabled = useFlag('enable-challenge-ui');
  const hasCartEntries = !!where.cartEntries?.length;

  useDebouncedEffect(
    () => {
      if (
        !isChallengeUiEnabled ||
        !loyaltyId ||
        !hasCartEntries ||
        isOffersEvaluating ||
        getCurrentPathname().startsWith(routes.cart)
      ) {
        return;
      }
      queryClient.prefetchQuery(evaluateChallengesQueryOptions({ loyaltyId, isChallengeUiEnabled, where }));
    },
    PREFETCH_EVALUATE_CHALLENGES_DEBOUNCE_MS,
    [getCurrentPathname, hasCartEntries, isChallengeUiEnabled, isOffersEvaluating, loyaltyId, where]
  );
};

export const loyaltyUserTransactionsQueryKeys = {
  all: ['loyaltyUserTransactions'] as const,
  loyaltyUser: ({ loyaltyId }: { loyaltyId?: string }) =>
    [...loyaltyUserTransactionsQueryKeys.all, loyaltyId] as const,
};

export const useLoyaltyUserTransactions = () => {
  const { loyaltyId } = useLoyaltyLegacyStates();

  return useQuery({
    enabled: !!loyaltyId,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    queryKey: loyaltyUserTransactionsQueryKeys.loyaltyUser({
      loyaltyId,
    }),
    queryFn: async () => {
      const response = await legacyClient.query<
        ILoyaltyUserTransactionsQuery,
        ILoyaltyUserTransactionsQueryVariables
      >({
        query: LoyaltyUserTransactionsDocument,
        variables: { loyaltyId },
        fetchPolicy: 'no-cache',
      });
      if (response.errors) {
        throw response.errors;
      }

      return (response.data?.loyaltyUserV2?.transactions ?? []) as ITransactionFragment[];
    },
  });
};

export const rewardsTiersQueryKey = ['rewardTiers'];

export const rewardsTiersQueryKeys = {
  all: [...rewardsTiersQueryKey, 'getRewardsTiers'] as const,
  loyaltyUser: ({ loyaltyId }: { loyaltyId?: string }) =>
    [...rewardsTiersQueryKeys.all, loyaltyId] as const,
};

export const useRewardTiers = () => {
  const enableRewardsTiers = useFlag('enable-rewards-tiers');
  const { loyaltyId, loyaltyUserReady } = useLoyaltyLegacyStates();

  return useQuery({
    enabled: enableRewardsTiers && loyaltyUserReady,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryKey: rewardsTiersQueryKeys.loyaltyUser({
      loyaltyId,
    }),
    queryFn: async () => {
      const response = await legacyClient.query<
        ILoyaltyRewardTiersQuery,
        ILoyaltyRewardTiersQueryVariables
      >({
        query: LoyaltyRewardTiersDocument,
        variables: { loyaltyId },
        fetchPolicy: 'no-cache',
      });
      if (response.errors) {
        throw response.errors;
      }
      return response.data.loyaltyRewardTiersV2 as ILoyaltyRewardTier[];
    },
  });
};

export const estimatedPointsEarnedQueryKeys = {
  all: ['estimatedPointsEarned'] as const,
  params: ({ loyaltyId, where }: { loyaltyId?: string; where: ILoyaltyEstimatePointsInput }) =>
    [...estimatedPointsEarnedQueryKeys.all, loyaltyId, where] as const,
};

export const useEstimatedPointsEarned = () => {
  const { loyaltyId, loyaltyUserReady } = useLoyaltyLegacyStates();
  const where = useLoyaltyCartInput();
  const isOffersEvaluating = useOffersLoadingWithTrailingSettle();

  const hasCartEntries = !!where.cartEntries?.length;

  const enabled =
    loyaltyUserReady && !!loyaltyId && hasCartEntries && where.subtotalAmount > 0 && !isOffersEvaluating;

  const query = useQuery({
    enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    meta: { persist: false },
    queryKey: estimatedPointsEarnedQueryKeys.params({ loyaltyId, where }),
    queryFn: async () => {
      const response = await legacyClient.query<
        ILoyaltyUserEstimatedPointsEarnedQuery,
        ILoyaltyUserEstimatedPointsEarnedQueryVariables
      >({
        query: LoyaltyUserEstimatedPointsEarnedDocument,
        variables: { loyaltyId, where },
        fetchPolicy: 'no-cache',
      });

      if (response.errors) {
        throw response.errors;
      }

      return response.data?.loyaltyUserV2?.estimatedPointsEarned ?? null;
    },
  });

  return { ...query, enabled };
};
